import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { Bar } from "react-chartjs-2";

import {
    Chart as ChartJS,
    BarElement,
    BarController,
    CategoryScale,
    LinearScale,
    Tooltip,
} from "chart.js";

import {
    useLanguage
} from "../contexts/LanguageContext";

ChartJS.register(
    BarElement,
    BarController,
    CategoryScale,
    LinearScale,
    Tooltip
);

const OMDB_RATING_SOURCES = [
    {
        key: "imdb",
        label: "IMDb",
        color: "#00FFFF",
    },
    {
        key: "rottenTomatoes",
        label: "Rotten Tomatoes",
        color: "#FF33CC",
    },
    {
        key: "metacritic",
        label: "Metacritic",
        color: "#D90093",
    },
];

const OMDB_COPY = {
    fr: {
        trigger: "Scores et données",
        title: "Regard externe",
        scores: "Scores agrégés",
        boxOffice: "Box-office",
        votes: "Votes IMDb",
        awards: "Récompenses",
        source: "Données fournies par OMDb",
        close: "Fermer les données OMDb",
    },

    en: {
        trigger: "Scores and data",
        title: "External insights",
        scores: "Aggregated scores",
        boxOffice: "Box office",
        votes: "IMDb votes",
        awards: "Awards",
        source: "Data provided by OMDb",
        close: "Close OMDb data",
    },

    es: {
        trigger: "Puntuaciones y datos",
        title: "Perspectiva externa",
        scores: "Puntuaciones agregadas",
        boxOffice: "Taquilla",
        votes: "Votos IMDb",
        awards: "Premios",
        source: "Datos proporcionados por OMDb",
        close: "Cerrar los datos de OMDb",
    },

    de: {
        trigger: "Bewertungen und Daten",
        title: "Externe Einblicke",
        scores: "Aggregierte Bewertungen",
        boxOffice: "Einspielergebnis",
        votes: "IMDb-Stimmen",
        awards: "Auszeichnungen",
        source: "Daten bereitgestellt von OMDb",
        close: "OMDb-Daten schließen",
    },
};

const OmdbInsightsPopover = ({
    data,
    movieTitle,
}) => {
    const { currentLanguage } = useLanguage();

    const popoverRef = useRef(null);

    const [
        isPopoverOpen,
        setIsPopoverOpen
    ] = useState(false);

    const languageKey =
        String(currentLanguage || "en")
            .split("-")[0];

    const copy =
        OMDB_COPY[languageKey] ||
        OMDB_COPY.en;

    const ratingEntries = useMemo(() => {
        return OMDB_RATING_SOURCES
            .map((source) => {
                const rating =
                    data?.ratings?.[source.key];

                const score =
                    Number(rating?.score);

                if (
                    !rating ||
                    !Number.isFinite(score)
                ) {
                    return null;
                }

                return {
                    ...source,
                    value: rating.value,
                    score,
                };
            })
            .filter(Boolean);
    }, [data]);

    const hasInsights = Boolean(
        ratingEntries.length > 0 ||
        data?.boxOffice ||
        data?.imdbVotes ||
        data?.awards
    );

    const popoverId =
        `omdb-insights-${data?.imdbId || "movie"}`;

    const chartData = useMemo(() => {
        return {
            labels: ratingEntries.map(
                (rating) =>
                    `${rating.label} · ${rating.value}`
            ),

            datasets: [
                {
                    data: ratingEntries.map(
                        (rating) => rating.score
                    ),

                    backgroundColor:
                        ratingEntries.map(
                            (rating) => rating.color
                        ),

                    borderColor: "#FFFFFF",
                    borderWidth: 1,
                    borderRadius: 12,
                    borderSkipped: false,
                    barThickness: 28,
                },
            ],
        };
    }, [ratingEntries]);

    const chartOptions = useMemo(() => {
        return {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,

            plugins: {
                legend: {
                    display: false,
                },

                tooltip: {
                    callbacks: {
                        label: (context) =>
                            `${context.parsed.x}%`,
                    },
                },
            },

            scales: {
                x: {
                    beginAtZero: true,
                    min: 0,
                    max: 100,

                    grid: {
                        color:
                            "rgba(255, 255, 255, 0.12)",
                    },

                    ticks: {
                        color: "#FFFFFF",

                        callback: (value) =>
                            `${value}%`,
                    },
                },

                y: {
                    grid: {
                        display: false,
                    },

                    ticks: {
                        color: "#FFFFFF",

                        font: {
                            weight: "bold",
                        },
                    },
                },
            },

            animation: {
                duration: 950,
                easing: "easeOutQuart",
            },
        };
    }, []);

    useEffect(() => {
        const popover =
            popoverRef.current;

        if (!popover) {
            return undefined;
        }

        const handleToggle = (event) => {
            setIsPopoverOpen(
                event.newState === "open"
            );
        };

        popover.addEventListener(
            "toggle",
            handleToggle
        );

        return () => {
            popover.removeEventListener(
                "toggle",
                handleToggle
            );
        };
    }, [
        hasInsights,
        data?.imdbId,
    ]);

    useEffect(() => {
        setIsPopoverOpen(false);
    }, [data?.imdbId]);

    const openPopover = () => {
        const popover =
            popoverRef.current;

        if (
            popover &&
            typeof popover.showPopover ===
                "function"
        ) {
            popover.showPopover();
        }
    };

    const closePopover = () => {
        const popover =
            popoverRef.current;

        if (
            popover &&
            typeof popover.hidePopover ===
                "function"
        ) {
            popover.hidePopover();
        }
    };

    if (!hasInsights) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                className="omdb-insights-trigger"
                aria-controls={popoverId}
                aria-expanded={isPopoverOpen}
                aria-haspopup="dialog"
                onClick={openPopover}
            >
                <span className="omdb-insights-trigger__source">
                    OMDb
                </span>

                <span>
                    {copy.trigger}
                </span>
            </button>

            <div
                ref={popoverRef}
                id={popoverId}
                className="omdb-insights-popover"
                popover="auto"
                role="dialog"
                aria-labelledby={`${popoverId}-title`}
            >
                <header className="omdb-insights-popover__header">
                    <div>
                        <span className="omdb-insights-popover__eyebrow">
                            OMDb
                        </span>

                        <h2 id={`${popoverId}-title`}>
                            {copy.title}
                        </h2>

                        <p>{movieTitle}</p>
                    </div>

                    <button
                        type="button"
                        className="omdb-insights-popover__close"
                        onClick={closePopover}
                        aria-label={copy.close}
                    >
                        ×
                    </button>
                </header>

                {isPopoverOpen &&
                    ratingEntries.length > 0 && (
                        <section className="omdb-insights-scores">
                            <h3>{copy.scores}</h3>

                            <div
                                className="omdb-insights-chart"
                                style={{
                                    "--omdb-rating-count":
                                        ratingEntries.length,
                                }}
                            >
                                <Bar
                                    key={`${data.imdbId}-${isPopoverOpen}`}
                                    data={chartData}
                                    options={chartOptions}
                                />
                            </div>
                        </section>
                    )}

                <div className="omdb-insights-stats">
                    {data.boxOffice && (
                        <article className="omdb-insights-stat">
                            <span>{copy.boxOffice}</span>
                            <strong>
                                {data.boxOffice}
                            </strong>
                        </article>
                    )}

                    {data.imdbVotes && (
                        <article className="omdb-insights-stat">
                            <span>{copy.votes}</span>
                            <strong>
                                {data.imdbVotes}
                            </strong>
                        </article>
                    )}

                    {data.awards && (
                        <article className="omdb-insights-stat omdb-insights-stat--wide">
                            <span>{copy.awards}</span>
                            <strong>
                                {data.awards}
                            </strong>
                        </article>
                    )}
                </div>

                <p className="omdb-insights-popover__source">
                    {copy.source}
                </p>
            </div>
        </>
    );
};

export default OmdbInsightsPopover;